import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';


import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {
    UserAiTokenLimitsView,
    UserCommonLimitsView,
    UserSearchLimitsView,
    useDefaultUserCommonLimits,
    useDefaultUserSearchLimits,
    useUserAiTokenLimits,
    useUserCommonLimits,
    useUserSearchLimits,
    useUserTemporaryLimits,
} from 'src/modules/usage-limits';

import {Group} from '@pb/kit-component-group';
import {useGateValue} from '@pb/kit-lib-statsig';

import type {ReactElement} from 'react';
import type {SaveOptions, SaveWithReason} from 'src/components/save-with-reason/save-with-reason-button';
import type {MessageContextType} from 'src/contexts/message-context';
import type {Error} from 'src/openapi/admin/models/Error';
import type {Limit} from 'src/openapi/admin/models/Limit';

type UserLimitsContent = {
    isLoading: boolean,
    loadingError: Error | null,
    isChunkChanged: boolean,
    busy: boolean,

    content: React.ReactNode,

    onSave: SaveWithReason,
    onDiscard: () => void,
};

type Props = {
    userId: number,
    children: (content: UserLimitsContent) => ReactElement,
    usedLimits?: Limit[],
    editDisabled?: boolean,
    onProceed: () => void,
    onFieldChange: (fieldName: string) => void,
};

export const UserLimitsController = ({userId, onProceed, children, usedLimits, editDisabled, onFieldChange}: Props) => {
    const [busy, setBusy] = useState<boolean>(false);
    const saving = useRef(false);
    const aiTokenLimits = useUserAiTokenLimits({userId});

    const {setErrorMessage} = useContext<MessageContextType>(MessageContext);
    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-limits',
        onProceed,
    });

    const {
        isLoading: isDefaultCommonLimitsLoading,
        loadingError: defaultCommonLimitsLoadingError,
        data: defaultCommonLimits,
    } = useDefaultUserCommonLimits({userId});

    const {
        isLoading: isCommonLimitsLoading,
        loadingError: commonLimitsLoadingError,
        isChanged: isUserCommonLimitsChanged,
        data: commonLimits,
        dataInitial: initialCommonLimits,
        setData: setCommonLimits,
        saveChanges: saveUserCommonLimits,
        discardChanges: discardUserCommonLimits,
    } = useUserCommonLimits({userId});

    const {
        isLoading: isTemporaryCommonLimitsLoading,
        loadingError: temporaryCommonLimitsError,
        isChanged: isCommonTemporaryLimitsChanged,
        data: tempLimitsSettings,
        dataInitial: initialTempLimitsSettings,
        setData: setTempLimitsSettings,
        saveChanges: saveCommonTemporaryLimits,
        discardChanges: discardTemporaryCommonLimits,
    } = useUserTemporaryLimits({userId});

    const {
        isLoading: isDefaultSearchLimitsLoading,
        loadingError: defaultSearchLimitsLoadingError,
        data: defaultSearchLimits,
    } = useDefaultUserSearchLimits({userId});

    const {
        isLoading: isSearchLimitsLoading,
        loadingError: searchLimitsLoadingError,
        isChanged: isSearchLimitsChanged,
        data: searchLimits,
        setData: setSearchLimits,
        dataInitial: initialSearchLimits,
        saveChanges: saveSearchLimits,
        discardChanges: discardSearchLimits,
    } = useUserSearchLimits({userId});

    useEffect(() => {
        const changed = isUserCommonLimitsChanged || isCommonTemporaryLimitsChanged ||
            isSearchLimitsChanged || aiTokenLimits.isChanged;
        setIsChunkChanged(changed);
    }, [isUserCommonLimitsChanged, isCommonTemporaryLimitsChanged,
        isSearchLimitsChanged, aiTokenLimits.isChanged, setIsChunkChanged]);

    const onSave = useCallback(async (options?: SaveOptions) => {
        if (saving.current) {
            return;
        }
        if (tempLimitsSettings.expiresOn && !tempLimitsSettings.limits.length) {
            setErrorMessage('At least one temporary limit must be specified');
            return 'At least one temporary limit must be specified';
        }
        const aiValidationError = aiTokenLimits.validateChanges();
        if (aiValidationError) {
            setErrorMessage(aiValidationError);
            return aiValidationError;
        }
        if (aiTokenLimits.isLoading || aiTokenLimits.loadingError) {
            setErrorMessage('PitchBook AI token limits have not loaded');
            return 'PitchBook AI token limits have not loaded';
        }

        saving.current = true;
        setBusy(true);
        try {
            const calls = [];
            if (isUserCommonLimitsChanged) {
                calls.push(await saveUserCommonLimits(initialCommonLimits, options));
            }
            if (isCommonTemporaryLimitsChanged) {
                calls.push(await saveCommonTemporaryLimits(initialTempLimitsSettings.limits, options));
            }
            if (aiTokenLimits.isChanged) {
                calls.push(await aiTokenLimits.saveChanges(options));
            }
            if (isSearchLimitsChanged) {
                calls.push(await saveSearchLimits(initialSearchLimits, options));
            }
            const errors = calls.filter(Boolean);
            if (errors.length) {
                setErrorMessage(errors.join(' '));
                return errors.join(' ');
            } else {
                proceed();
            }
        } catch {
            setErrorMessage('Unable to save user limits');
            return 'Unable to save user limits';
        } finally {
            saving.current = false;
            setBusy(false);
        }
    }, [
        aiTokenLimits,
        tempLimitsSettings,
        isUserCommonLimitsChanged,
        saveUserCommonLimits,
        isCommonTemporaryLimitsChanged,
        saveCommonTemporaryLimits,
        isSearchLimitsChanged,
        saveSearchLimits,
        initialCommonLimits,
        initialTempLimitsSettings,
        initialSearchLimits,
        proceed,
        setErrorMessage,
    ]);

    const onDiscard = useCallback(() => {
        if (saving.current) {
            return;
        }
        discardUserCommonLimits();
        discardTemporaryCommonLimits();
        discardSearchLimits();
        aiTokenLimits.discardChanges();
    }, [discardUserCommonLimits, discardTemporaryCommonLimits, discardSearchLimits, aiTokenLimits]);

    const isPB_AI_Enabled = useGateValue(STATSIG_GATES.PB_AI_LIMITS);
    const disabled = editDisabled || busy;
    const isCommonLimitsViewLoading = isCommonLimitsLoading || isDefaultCommonLimitsLoading ||
        isTemporaryCommonLimitsLoading;

    const commonLimitsViewLoadingError = commonLimitsLoadingError || defaultCommonLimitsLoadingError ||
        temporaryCommonLimitsError;

    const isSearchLimitsViewLoading = isSearchLimitsLoading || isDefaultSearchLimitsLoading;
    const searchLimitsViewLoadingError = searchLimitsLoadingError || defaultSearchLimitsLoadingError;
    const isLoading = isCommonLimitsLoading || isTemporaryCommonLimitsLoading || isSearchLimitsLoading ||
        isDefaultCommonLimitsLoading || isDefaultSearchLimitsLoading || aiTokenLimits.isLoading;

    const loadingError = commonLimitsLoadingError || temporaryCommonLimitsError || searchLimitsLoadingError ||
        defaultCommonLimitsLoadingError || defaultSearchLimitsLoadingError || aiTokenLimits.loadingError;

    return children({
        busy,
        isLoading,
        loadingError,
        isChunkChanged: isCurrentChunkChanged,
        content: (
            <Group direction="v" gap="l">
                <UserCommonLimitsView {...{
                    disabled,
                    isLoading: isCommonLimitsViewLoading,
                    loadingError: commonLimitsViewLoadingError,
                    usedLimits,
                    licenseLimits: defaultCommonLimits,
                    commonLimits,
                    setCommonLimits,
                    tempLimitsSettings,
                    setTempLimitsSettings,

                    onFieldChange,
                }}
                />
                {
                    isPB_AI_Enabled ? (
                        <UserAiTokenLimitsView
                            limits={aiTokenLimits}
                            disabled={disabled}
                            onFieldChange={onFieldChange}
                        />
                    ) : null
                }
                <UserSearchLimitsView {...{
                    disabled,
                    isLoading: isSearchLimitsViewLoading,
                    loadingError: searchLimitsViewLoadingError,
                    defaultSearchLimits,
                    usedLimits,
                    userSearchLimits: searchLimits,
                    setUserSearchLimits: setSearchLimits,

                    onFieldChange,
                }}
                />
            </Group>
        ),
        onSave,
        onDiscard,
    });
};
